import { DataTableColumnHeader } from "./donor/data-table-column-header";
/**
 * The selectable record row — the one piece of the records layer that five
 * pages hand-rolled identically before this file existed.
 *
 * A record list is a table at `md` and up and a card list below it, because a
 * four-column academic table has nowhere to go at 375px but sideways. Both
 * forms open the same URL-addressable record, so `useRecordSheetSelection`
 * keeps working unchanged and the deep link is width-independent.
 *
 * Columns are descriptors and the card body is a render prop because the
 * columns genuinely differ per record type — a student has a GPA, an import run
 * has a digest. That difference is why the row resisted abstraction for five
 * pages, so it stays a composition seam. This layer is not configured by a
 * page-description schema and must not become one.
 *
 * Behaviour lives in `record-table-engine.ts`. Sorting, faceted filtering,
 * column visibility, row selection, and pagination are all off unless a page
 * asks for them; a page that asks for none renders exactly what it rendered
 * before the engine existed.
 */
import { useId, type ReactNode } from "react";


import { RecordSelectionCheckbox } from "./RecordSelectionCheckbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import {
  PRIMARY_COLUMN_ID,
  useRecordTable,
  type RecordColumn,
  type RecordPrimaryColumn,
  type RecordSortDirection,
  type RecordTableEngine,
  type RecordTableOptions,
} from "./record-table-engine";
import {
  RecordSelectionBar,
  RecordTablePagination,
  RecordTableToolbar,
} from "./RecordTableControls";

// Types only. `PRIMARY_COLUMN_ID` and `useRecordTable` are values, and
// re-exporting a value beside a component costs the file its fast refresh —
// pages that need them import from `record-table-engine` directly.
export type {
  RecordColumn,
  RecordFacet,
  RecordFacetOption,
  RecordFacetSelection,
  RecordPrimaryColumn,
  RecordSort,
  RecordSortDirection,
  RecordTableEngine,
  RecordTableManualModes,
  RecordTableOptions,
} from "./record-table-engine";
export {
  RecordSelectionBar,
  RecordTablePagination,
  RecordTableToolbar,
} from "./RecordTableControls";

// A mobile record is a row inside the workspace tile. The public component
// names remain stable for the course pickers that use the same record grammar.
const CARD_INSET = "ps-5 pe-4";
const CARD_SHELL = `min-h-24 min-w-0 border-b border-rule ${CARD_INSET} py-3 text-left last:border-b-0`;

/**
 * The selectable card. Exported on its own because the instructor and student
 * course pickers are card-only at every width — they have no table form to
 * switch away from, but they are the same selectable record.
 */
export function RecordCard({
  selected,
  onSelect,
  ariaLabel,
  rowId,
  children,
  className,
}: {
  selected: boolean;
  onSelect: () => void;
  ariaLabel?: string;
  rowId?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      data-act="visit"
      data-row={rowId}
      data-record-current={selected || undefined}
      onClick={onSelect}
      aria-label={ariaLabel}
      aria-pressed={selected}
      className={cn(
        CARD_SHELL,
        "transition-colors duration-[var(--motion-fast)] hover:bg-well outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring aria-pressed:bg-primary-wash",
        className,
      )}
    >
      {children}
    </button>
  );
}

/**
 * The card a page can check for a bulk action.
 *
 * A checkbox inside `RecordCard` would be a checkbox inside a button: invalid
 * markup, and inoperable with it — the outer button takes the click and the
 * control it contains never sees one. So the shell stops being the pressable
 * thing and becomes a container holding two siblings, the checkbox and the open
 * control.
 *
 * It stays local rather than becoming a variant of `RecordCard` because the two
 * course pickers import that component for the shell it is today, and growing
 * that shell a checkbox column is a change to a list that asked for nothing.
 */
function SelectableRecordCard({
  label,
  checked,
  onCheckedChange,
  isOpen,
  onOpen,
  openAriaLabel,
  rowId,
  action,
  children,
  className,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: () => void;
  isOpen: boolean;
  onOpen?: () => void;
  openAriaLabel?: string;
  rowId: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      // The same `data-state` the table row carries, and for the same reason.
      // Being checked for a bulk action and being the record the sheet is
      // currently showing are two different facts about one card, so the
      // checkbox reports the first and the shell reports the second; one
      // treatment doing both would leave a reader unable to tell which.
      data-row={rowId}
      data-record-checked={checked || undefined}
      data-state={isOpen ? "selected" : undefined}
      className={cn(
        CARD_SHELL,
        // No `items-start` on the row. It pins every child to its own content
        // height, which left the open control 52px tall inside a 96px card:
        // the hover treatment lit the whole card while only the top third took
        // the tap, and `RecordCard` opened from anywhere. The row stretches and
        // the checkbox opts out, because the checkbox is the one thing here that
        // wants top alignment rather than height.
        "flex gap-3 data-[state=selected]:bg-primary-wash",
        onOpen && "transition-colors duration-[var(--motion-fast)] hover:bg-well",
        className,
      )}
    >
      <RecordSelectionCheckbox
        checked={checked}
        onCheckedChange={onCheckedChange}
        aria-label={label}
        className="self-start"
      />
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          aria-label={openAriaLabel}
          data-act="visit"
          aria-pressed={isOpen}
          className="min-w-0 flex-1 rounded-control text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {children}
        </button>
      ) : (
        // Not a button, for the reason the un-openable card is not one. Adding a
        // checkbox gives the reader something to do with this card; it does not
        // give the body somewhere to go.
        <div className="min-w-0 flex-1">{children}</div>
      )}
      {action}
    </div>
  );
}

export function RecordCardList({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn("grid min-w-0", className)}>{children}</div>;
}

interface RecordTableViewProps<T> {
  /**
   * Absent where the list opens nothing. A queue whose row action is "resume",
   * not "open the record", would otherwise get a focusable primary column that
   * goes nowhere — the layer must be able to render a row that is evidence
   * rather than a door.
   */
  onOpen?: (id: string) => void;
  selectedId?: string | null;
  /** The card body below `md`. The layer supplies the shell, the open control, and the checkbox. */
  card: (row: T) => ReactNode;
  /** Rendered beside the selection count once rows are checked. */
  bulkActions?: ReactNode;
  /** An existing native bulk Task may already own the exact selected/hidden summary. */
  selectionSummary?: boolean;
  /** One toolbar over this exact engine, including successful empty reads. */
  toolbar?: (engine: RecordTableEngine<T>) => ReactNode;
  /** Compose tools, area count, rows and supported pagination in one working surface. */
  enclosure?: boolean;
  /** Area-owned truthful result count or scope, inside that same surface. */
  summary?: ReactNode;
  /** Secondary exact work beside the mobile open target, and in a desktop action cell. */
  rowAction?: (row: T) => ReactNode;
  /** The empty state when a capability, rather than the fetch, emptied the list. */
  emptyState?: ReactNode;
}

type RecordTableProps<T> = RecordTableOptions<T> & RecordTableViewProps<T>;

export function RecordTable<T>({
  selectedId,
  onOpen,
  card,
  bulkActions,
  selectionSummary,
  toolbar,
  enclosure,
  summary,
  rowAction,
  emptyState,
  ...options
}: RecordTableProps<T>) {
  const engine = useRecordTable(options);
  return (
    <RecordTableView
      engine={engine}
      selectedId={selectedId}
      onOpen={onOpen}
      card={card}
      bulkActions={bulkActions}
      selectionSummary={selectionSummary}
      toolbar={toolbar}
      enclosure={enclosure}
      summary={summary}
      rowAction={rowAction}
      emptyState={emptyState}
    />
  );
}

/**
 * The same view against an engine the page already built. A page needs this
 * only when something outside the table drives the table's state — Admissions'
 * pipeline strip sets the stage facet, and it sits above `DataWorkspace`.
 */
export function RecordTableView<T>({
  engine,
  selectedId = null,
  onOpen,
  card,
  bulkActions,
  selectionSummary = true,
  toolbar,
  enclosure = false,
  summary,
  rowAction,
  emptyState,
}: RecordTableViewProps<T> & { engine: RecordTableEngine<T> }) {
  const { primary, rowClassName, rowId, rows, visibleColumns } = engine;
  // The card list's select-all needs a real `label`, and a page may render two
  // lists — the design-system specimen renders one per tenant side by side, and
  // a shared literal id would point both labels at the first checkbox.
  const pageSelectId = useId();

  // Both forms, from one expression, so they cannot disagree about whether the
  // page can be taken. A list with `selectable` and facets but no `emptyState`
  // falls through to the main branch when a filter empties it, and a select-all
  // over nothing selects nothing — the dead control this file spends a paragraph
  // arguing against fifty lines further down.
  const canSelectPage = engine.selectable && rows.length > 0;
  const frame = (children: ReactNode) => enclosure ? <div data-record-table-enclosure="">{children}</div> : children;
  const tools = toolbar ? toolbar(engine) : <RecordTableToolbar engine={engine} />;
  const heading = <>{tools && <div data-record-table-tools="">{tools}</div>}{summary != null && <div data-record-table-summary="">{summary}</div>}</>;


  // The toolbar stays when a capability empties the list, because the control
  // that emptied it is the only way back. A page's own empty state can't cover
  // this: it tests the rows it fetched, and those are still there.
  //
  // The selection bar stays for the harder version of the same problem. A
  // selection made before the filter narrowed the list is still in the engine
  // and still what a bulk action would act on, so dropping the bar hides a live
  // count, takes away the action, and removes the only control that can clear
  // it — leaving state the reader can neither see nor undo.
  if (rows.length === 0 && emptyState) {
    return frame(
      <>
        {heading}
        {selectionSummary && <RecordSelectionBar engine={engine} actions={bulkActions} />}
        <div data-record-table-empty="">{emptyState}</div>
        <RecordTablePagination engine={engine} />
      </>
    );
  }

  return frame(
    <>
      {heading}
      {selectionSummary && <RecordSelectionBar engine={engine} actions={bulkActions} />}

      <div className="md:hidden">
        <RecordCardList>
          {canSelectPage && (
            // One select-all target for the mobile rows, linked to a real label.
            <div className={cn("flex items-center gap-3 border-b border-rule py-2", CARD_INSET)}>
              <RecordSelectionCheckbox
                id={pageSelectId}
                checked={pageChecked(engine)}
                onCheckedChange={engine.togglePageSelected}
                aria-label="Select every record on this page"
              />
              {/* A lone checkbox over a card list says nothing; the table's says
                  it by sitting in a header row, and there is no header row here.
                  A real `label`, because text that sits beside a checkbox and is
                  styled like its label is where a thumb goes first — and the
                  words are a prefix of the accessible name rather than a shorter
                  paraphrase, so someone speaking what they can read reaches the
                  control they can see. */}
              <label htmlFor={pageSelectId} className="text-ui text-muted-foreground">
                Select every record
              </label>
            </div>
          )}
          {rows.map((row) => {
            const id = rowId(row);
            if (engine.selectable) {
              return (
                <SelectableRecordCard
                  key={id}
                  rowId={id}
                  action={rowAction?.(row)}
                  label={selectionLabel(primary, row, id)}
                  checked={engine.isRowSelected(id)}
                  onCheckedChange={() => engine.toggleRowSelected(id)}
                  isOpen={selectedId === id}
                  onOpen={onOpen && (() => onOpen(id))}
                  openAriaLabel={primary.ariaLabel?.(row)}
                  className={rowClassName?.(row)}
                >
                  {card(row)}
                </SelectableRecordCard>
              );
            }
            if (!onOpen) {
              // Not a button. A card that looks pressable and does nothing is
              // worse than a card that does not, and it puts a dead stop in the
              // keyboard path of every row.
              return (
                <div key={id} data-row={id} className={cn(CARD_SHELL, rowClassName?.(row))}>
                  {card(row)}
                  {rowAction?.(row)}
                </div>
              );
            }
            if (rowAction) return (
              <div key={id} data-row={id} className="flex min-w-0 items-stretch border-b border-rule last:border-b-0">
                <RecordCard rowId={id} selected={selectedId === id} onSelect={() => onOpen(id)} ariaLabel={primary.ariaLabel?.(row)} className="min-w-0 flex-1 border-0">{card(row)}</RecordCard>
                <div className="flex shrink-0 items-center pe-4">{rowAction(row)}</div>
              </div>
            );
            return (
              <RecordCard
                key={id}
                rowId={id}
                selected={selectedId === id}
                onSelect={() => onOpen(id)}
                ariaLabel={primary.ariaLabel?.(row)}
                className={rowClassName?.(row)}
              >
                {card(row)}
              </RecordCard>
            );
          })}
        </RecordCardList>
      </div>

      <div className="hidden overflow-x-auto md:block">
        <Table>
          <TableHeader>
            <TableRow>
              {canSelectPage && (
                <TableHead className="w-10">
                  <RecordSelectionCheckbox
                    checked={pageChecked(engine)}
                    onCheckedChange={engine.togglePageSelected}
                    aria-label="Select every record on this page"
                  />
                </TableHead>
              )}
              <TableHead aria-sort={ariaSort(engine.primarySortable, engine.primaryDirection)}>
                <SortableHeader
                  sortable={engine.primarySortable}
                  direction={engine.primaryDirection}
                  name={typeof primary.header === "string" ? primary.header : primary.name}
                  column={engine.table.getColumn(PRIMARY_COLUMN_ID)!}
                >
                  {primary.header}
                </SortableHeader>
              </TableHead>
              {visibleColumns.map(({ id, column, sortable, direction }) => (
                <TableHead
                  key={id}
                  aria-sort={ariaSort(sortable, direction)}
                  className={alignClass(column.align)}
                >
                  <SortableHeader
                    sortable={sortable}
                    direction={direction}
                    align={column.align}
                    name={typeof column.header === "string" ? column.header : column.name}
                    column={engine.table.getColumn(id)!}
                  >
                    {column.header}
                  </SortableHeader>
                </TableHead>
              ))}
              {rowAction && <TableHead><span className="sr-only">Actions</span></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const id = rowId(row);
              const detail = primary.detail?.(row);
              return (
                <TableRow
                  key={id}
                  data-row={id}
                  data-record-checked={engine.selectable && engine.isRowSelected(id) || undefined}
                  data-state={selectedId === id ? "selected" : undefined}
                  className={rowClassName?.(row)}
                >
                  {engine.selectable && (
                    <TableCell className="w-10">
                      <RecordSelectionCheckbox
                        checked={engine.isRowSelected(id)}
                        onCheckedChange={() => engine.toggleRowSelected(id)}
                        aria-label={selectionLabel(primary, row, id)}
                      />
                    </TableCell>
                  )}
                  <TableCell className="min-w-0 whitespace-normal">
                    {onOpen ? (
                      <button
                        type="button"
                        data-act="visit"
                        onClick={() => onOpen(id)}
                        aria-label={primary.ariaLabel?.(row)}
                        aria-pressed={selectedId === id}
                        className="inline-flex min-h-[var(--control-height-compact)] items-center rounded-control break-words text-left font-medium text-foreground outline-none hover:text-primary focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {primary.label(row)}
                      </button>
                    ) : (
                      <span className="font-medium text-foreground">{primary.label(row)}</span>
                    )}
                    {detail != null && (
                      <div className="mt-0.5 max-w-80 break-words text-meta text-muted-foreground">
                        {detail}
                      </div>
                    )}
                  </TableCell>
                  {visibleColumns.map(({ id: columnId, column }) => (
                    <TableCell
                      key={columnId}
                      className={cn(alignClass(column.align), column.cellClassName)}
                    >
                      {column.cell(row)}
                    </TableCell>
                  ))}
                  {rowAction && <TableCell>{rowAction(row)}</TableCell>}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <RecordTablePagination engine={engine} />
    </>
  );
}

/** Header and cell take their alignment from one place so they cannot drift. */
function alignClass(align: RecordColumn<unknown>["align"]): string | undefined {
  if (align === "right") return "text-right";
  if (align === "center") return "text-center";
  return undefined;
}

/**
 * The primary label as a string, when it is one. A label that composes elements
 * has no text the layer can lift without rendering it, and guessing would be
 * worse than falling through to the descriptor's own `ariaLabel`.
 */
function plainLabel(label: ReactNode): string | null {
  return typeof label === "string" || typeof label === "number" ? String(label) : null;
}

/**
 * What a row's selection checkbox is called, in both forms.
 *
 * The row's name, not its key. Every id on a real page is a UUID, and "select
 * record 11111111-1111-…" identifies nothing to anyone listening rather than
 * looking. It is computed here rather than at each call site because the table
 * and the card are one selection mechanism seen at two widths: naming the same
 * record two ways would make them two, and the drift would only ever show up to
 * the reader who cannot see which width they are on.
 */
function selectionLabel<T>(primary: RecordPrimaryColumn<T>, row: T, id: string): string {
  return `Select ${primary.ariaLabel?.(row) ?? plainLabel(primary.label(row)) ?? id}`;
}

/**
 * A part-selected page is a third state, not an unchecked one. Collapsing it to
 * `false` shows and announces "nothing selected" to a reader who has selected
 * something, so both page-level controls read it from here and only one of them
 * can be wrong.
 */
function pageChecked<T>(engine: RecordTableEngine<T>): boolean | "indeterminate" {
  if (engine.pageSelectionState === "all") return true;
  return engine.pageSelectionState === "some" ? "indeterminate" : false;
}

/**
 * `aria-sort` belongs on the header cell, and only on a column that sorts —
 * "none" on every other column would tell a screen reader that a dozen columns
 * are sortable and simply unsorted.
 */
function ariaSort(
  sortable: boolean,
  direction: RecordSortDirection | null,
): "ascending" | "descending" | "none" | undefined {
  if (!sortable) return undefined;
  return direction === "asc" ? "ascending" : direction === "desc" ? "descending" : "none";
}

/**
 * A header is a button only when its column can actually sort. Wrapping every
 * header would put a dozen no-op stops in the keyboard path of a list where two
 * columns sort, and it would promise an affordance the column does not have.
 */
function SortableHeader<T>({ sortable, align, name, column, children }: {
  sortable: boolean;
  direction: RecordSortDirection | null;
  align?: RecordColumn<unknown>["align"];
  name?: string;
  column: import("@tanstack/react-table").Column<T>;
  children: ReactNode;
}) {
  if (!sortable) return <>{children}</>;
  return (
    <DataTableColumnHeader
      column={column}
      title={name ?? (typeof children === "string" ? children : "Column")}
      sortClearLabel="Original order"
      className={align === "right" ? "justify-end" : undefined}
    />
  );
}
